import React, { Suspense, lazy } from 'react';
import { ArrowRight, ChevronDown, Play } from 'lucide-react';
import { stats } from '../../data/mock';

// Lazy load Spline for performance
const Spline = lazy(() => import('@splinetool/react-spline'));

const HeroSection = ({ type }) => {
  const content = {
    industries: {
      headline: "Transform Your",
      highlight: "Operations.",
      subheadline: "Drive Excellence.",
      description: "For over 15 years, we've helped Canadian businesses streamline processes, reduce waste, and achieve operational excellence through proven methodologies.",
      primaryCta: "Start Your Transformation",
      secondaryCta: "Our Services",
      backgroundImage: "https://images.unsplash.com/photo-1738162837369-a2beec3a1d47?auto=format&fit=crop&w=1920&q=80"
    },
    innovation: {
      headline: "Turn Innovation",
      highlight: "Into Cash.",
      subheadline: "Refunds & Funding.",
      description: "We help Canadian companies translate everyday problem-solving and improvements into SR&ED tax credits and innovation funding. You're probably already doing work that qualifies.",
      primaryCta: "Estimate Your Refund",
      secondaryCta: "Learn About SR&ED",
      backgroundImage: "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?auto=format&fit=crop&w=1920&q=80"
    }
  };

  const { headline, highlight, subheadline, description, primaryCta, secondaryCta, backgroundImage } = content[type];

  return (
    <section id="home" className="relative min-h-screen bg-black flex items-center pt-20 overflow-hidden">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0 z-0">
        <img 
          src={backgroundImage} 
          alt="" 
          className="w-full h-full object-cover opacity-20"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/90 to-black/70" />
      </div>

      {/* Animated Grid Pattern */}
      <div className="absolute inset-0 z-0 opacity-[0.03]">
        <div className="absolute inset-0" style={{
          backgroundImage: `
            linear-gradient(rgba(74, 144, 164, 0.3) 1px, transparent 1px),
            linear-gradient(90deg, rgba(74, 144, 164, 0.3) 1px, transparent 1px)
          `,
          backgroundSize: '50px 50px'
        }} />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-20 lg:py-32 w-full">
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Left Content */}
          <div className="flex flex-col">
            {/* Badge - Fixed height */}
            <div className="h-12 mb-6">
              <div className="inline-flex items-center gap-2 bg-[#4A90A4]/10 border border-[#4A90A4]/30 px-4 py-2">
                <div className="w-2 h-2 bg-[#4A90A4] rounded-full animate-pulse" />
                <span className="text-[#4A90A4] text-sm font-medium">
                  {type === 'industries' ? '15+ Years of Excellence' : 'Recover Up to 64% of R&D Costs'}
                </span>
              </div>
            </div>

            {/* Headline Section - Fixed height */}
            <div className="min-h-[280px] lg:min-h-[320px] mb-6">
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-[1.1]">
                {headline}
                <span className="block text-[#4A90A4]">{highlight}</span>
                <span className="block text-white/80 text-4xl md:text-5xl lg:text-6xl font-semibold mt-2">{subheadline}</span>
              </h1>
              <p className="text-white/60 text-lg md:text-xl max-w-xl leading-relaxed mt-4">
                {description}
              </p>
            </div>

            {/* Buttons - Now at consistent position */}
            <div className="flex flex-col sm:flex-row gap-4 mb-8">
              <a
                href={type === 'innovation' ? '#calculator' : '#contact'}
                className="group bg-[#4A90A4] text-white px-8 py-4 font-medium flex items-center justify-center gap-3 hover:bg-[#5BA0B4] transition-all duration-300 hover:shadow-lg hover:shadow-[#4A90A4]/25"
              >
                {primaryCta}
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform duration-300" />
              </a>
              <a
                href={type === 'innovation' ? '#sred-basics' : '#services'}
                className="group bg-white/5 backdrop-blur-sm text-white px-8 py-4 font-medium flex items-center justify-center gap-3 hover:bg-white/10 border border-white/10 transition-all duration-300"
              >
                <Play size={16} className="text-[#4A90A4]" />
                {secondaryCta}
              </a>
            </div>

            {/* Trust Indicators */}
            <div className="flex items-center gap-8">
              <div className="flex -space-x-3">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="w-10 h-10 rounded-full bg-gradient-to-br from-[#4A90A4] to-[#2D5A6B] border-2 border-black flex items-center justify-center text-white text-xs font-bold">
                    {i === 4 ? '99+' : ''}
                  </div>
                ))}
              </div>
              <div>
                <p className="text-white font-semibold">500+ Projects</p>
                <p className="text-white/50 text-sm">Trusted by Canadian businesses</p>
              </div>
            </div>
          </div>

          {/* Right - 3D Spline or Stats */}
          <div className="relative">
            {type === 'innovation' ? (
              <div className="relative w-full h-[500px] lg:h-[600px]">
                <Suspense fallback={
                  <div className="w-full h-full flex items-center justify-center">
                    <div className="w-16 h-16 border-4 border-[#4A90A4]/30 border-t-[#4A90A4] rounded-full animate-spin" />
                  </div>
                }>
                  <Spline 
                    scene="https://prod.spline.design/NbVmy6DPLhY-5Lvg/scene.splinecode"
                    style={{ width: '100%', height: '100%' }}
                  />
                </Suspense>
                {/* Floating Stats on 3D */}
                <div className="absolute bottom-8 left-8 bg-black/80 backdrop-blur-md border border-white/10 p-4">
                  <p className="text-[#4A90A4] text-3xl font-bold">$10M+</p>
                  <p className="text-white/60 text-sm">Funding Recovered</p>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                {stats.map((stat, index) => (
                  <div
                    key={index}
                    className="group bg-white/5 backdrop-blur-sm border border-white/10 p-6 lg:p-8 hover:border-[#4A90A4]/50 hover:bg-[#4A90A4]/5 transition-all duration-500"
                  >
                    <div className="text-4xl md:text-5xl font-bold text-[#4A90A4] mb-2 group-hover:scale-105 transition-transform duration-300">
                      {stat.value}
                    </div>
                    <div className="text-white/50 text-sm uppercase tracking-wider">
                      {stat.label}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="flex justify-center mt-16 lg:mt-20">
          <a
            href={type === 'innovation' ? '#sred-basics' : '#services'}
            className="text-white/30 hover:text-[#4A90A4] transition-colors duration-300 animate-bounce"
          >
            <ChevronDown size={32} />
          </a>
        </div>
      </div>

      {/* Decorative Elements */}
      <div className="absolute top-1/4 right-0 w-96 h-96 bg-[#4A90A4]/10 rounded-full blur-[128px] pointer-events-none" />
      <div className="absolute bottom-0 left-1/4 w-64 h-64 bg-[#4A90A4]/5 rounded-full blur-[100px] pointer-events-none" />
    </section>
  );
};

export default HeroSection;
